import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'

export interface AuthUser {
	id: number
	email: string
	firstName?: string
	lastName?: string
	profilePicture?: string | null
}

interface AuthState {
	token: string | null
	user: AuthUser | null
}

interface AuthContextValue extends AuthState {
	setAuth: (auth: AuthState) => void
	logout: () => void
	isLoading: boolean
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined)

const STORAGE_KEY = 'gp_auth'

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [token, setToken] = useState<string | null>(null)
	const [user, setUser] = useState<AuthUser | null>(null)
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		const raw = localStorage.getItem(STORAGE_KEY)
		if (raw) {
			try {
				const parsed: AuthState = JSON.parse(raw)
				setToken(parsed.token)
				setUser(parsed.user)
			} catch {}
		}
		setIsLoading(false)
	}, [])

	const setAuth = useCallback((auth: AuthState) => {
		setToken(auth.token)
		setUser(auth.user)
		localStorage.setItem(STORAGE_KEY, JSON.stringify(auth))
	}, [])

	const logout = useCallback(() => {
		setToken(null)
		setUser(null)
		localStorage.removeItem(STORAGE_KEY)
	}, [])

	const value = useMemo<AuthContextValue>(
		() => ({ token, user, setAuth, logout, isLoading }),
		[token, user, setAuth, logout, isLoading],
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
	const ctx = useContext(AuthContext)
	if (!ctx) throw new Error('useAuth must be used within AuthProvider')
	return ctx
}
